import { Drawer, Rate, Input, Spin } from 'antd';
import { useState } from 'react';
import { useAuth } from '../../../../../../store/AuthContext/AuthContext';
import { authRequest } from '../../../../../../common/request/authRequest';
import { ButtonLZ } from '../../../../../components/design/Buttons/ButtonLZ';
import { LoadingOutlined } from '@ant-design/icons';
import { LabelView, LABEL_COLOR } from '../Label';
import { DrawerPanelTitle, drawerPanelStyles } from '../../../../common/Drawers/DrawerPanelTitle';

const SENTIMENT_LABEL: Record<string, string> = {
	POSITIVE: 'Positivo',
	NEUTRAL: 'Neutro',
	NEGATIVE: 'Negativo',
};

const stepNumberStyle = {
	color: '#86868b',
	fontWeight: 500 as const,
	fontSize: 12,
	textTransform: 'uppercase' as const,
	letterSpacing: '1.2px',
	display: 'block' as const,
	marginBottom: 4,
};

const stepTitleStyle = {
	color: '#1d1d1f',
	fontSize: 20,
	fontWeight: 600 as const,
	marginBottom: 16,
	marginTop: 0,
	lineHeight: 1.3,
};

export const TestLabelWithAIDrawer = (props: { openDrawer: boolean, setOpenDrawer: any, locationId?: any }) => {

	const [isLoading, setIsLoading] = useState(false);
	const [score, setScore] = useState(0);
	const [text, setText] = useState('');
	const [labels, setLabels] = useState<any>(null);
	const [hasResult, setHasResult] = useState(false);

	const { projectGroup } = useAuth();

	const hasScore = score > 0;
	const hasText = text?.trim() !== '';
	const isStep1Complete = hasScore && hasText;
	const isButtonEnabled = isStep1Complete && !isLoading;

	const labelWithIA = () => {
		if (!isButtonEnabled) return;
		const data = {
			text: text,
			score: score
		};
		setIsLoading(true);
		authRequest(`${process.env.REACT_APP_API}/projects/${projectGroup.project}/${projectGroup.group}/comments/get-labels`, { method: 'POST', data })
			.then((response: any) => {
				setLabels(response);
				setHasResult(true);
			})
			.catch(() => {
				setLabels([]);
				setHasResult(true);
			})
			.finally(() => {
				setIsLoading(false);
			});
	}

	const modoPruebaBadge = (
		<div style={{
			display: 'inline-flex',
			alignItems: 'center',
			padding: '4px 10px',
			borderRadius: 8,
			background: '#e8e8ed',
			border: '1px solid #8e8e93',
			color: '#3a3a3c',
			fontWeight: 600,
			fontSize: 12,
			textTransform: 'uppercase',
			letterSpacing: '1.2px',
		}}>
			Modo Prueba
		</div>
	);

	return (
		<Drawer
			placement="right"
			width={'40%'}
			title={
				<DrawerPanelTitle
					title="Etiquetado con IA"
					extra={modoPruebaBadge}
				/>
			}
			onClose={() => props.setOpenDrawer(false)}
			open={true}
			{...drawerPanelStyles}
		>
			<div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
				<div style={{ color: '#636366' }}>
					En esta pantalla podrás probar tu configuración del Agente de IA, probando diferentes textos y puntuaciones y validando los resultados.
				</div>

				<section>
					<span style={stepNumberStyle}>Paso 01</span>
					<div style={stepTitleStyle}>Escribe tu texto y puntuación</div>

					<div style={{ width: '88%', margin: '0 auto' }}>
						<div
							style={{
								width: '100%',
								padding: '14px 16px',
								borderRadius: 10,
								background: '#e8f0f8',
								border: `1px solid ${isStep1Complete ? '#86efac' : '#c5d6e8'}`,
								overflow: 'hidden',
							}}
						>
							<div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
								<div style={{
									padding: '4px 8px',
									borderRadius: 8,
									background: hasScore ? '#ffffff' : '#fff7ed',
									border: `1px solid ${hasScore ? '#c5d6e8' : '#fdba74'}`,
								}}>
									<Rate
										className="test-label-rate"
										style={{ fontSize: 18, color: hasScore ? '#475569' : '#ea580c' }}
										value={score}
										onChange={(value) => {
											setScore(value);
											setHasResult(false);
											setLabels(null);
										}}
									/>
								</div>
							</div>

							<Input.TextArea
								className='input-field'
								rows={5}
								value={text}
								placeholder="Escribe aquí el texto de la reseña…"
								onChange={(e) => {
									setText(e.target.value);
									setHasResult(false);
									setLabels(null);
								}}
								style={{
									width: '100%',
									background: '#ffffff',
									border: `1px solid ${hasText ? '#c5d6e8' : '#fdba74'}`,
									borderRadius: 8,
								}}
							/>
						</div>
					</div>
				</section>

				<style>{`
					.test-label-rate .ant-rate-star-first,
					.test-label-rate .ant-rate-star-second {
						color: #d4d4d8;
					}
					.test-label-rate .ant-rate-star-full .ant-rate-star-second,
					.test-label-rate .ant-rate-star-half .ant-rate-star-first {
						color: inherit;
					}
				`}</style>

				<section>
					<span style={stepNumberStyle}>Paso 02</span>
					<div style={stepTitleStyle}>Etiqueta con IA</div>

					{!isStep1Complete && (
						<div style={{
							fontSize: 12,
							color: '#94a3b8',
							marginBottom: 10,
						}}>
							Completa la puntuación y el texto del Paso 01 para continuar
						</div>
					)}

					<div style={{ display: 'flex', justifyContent: 'flex-start' }}>
						<ButtonLZ
							onClick={labelWithIA}
							disabled={!isButtonEnabled}
							type='ghost'
							size='small'
							style={isButtonEnabled ? {
								fontSize: 12,
								border: '1px solid #e9d5ff',
								background: '#faf5ff',
								color: '#7e22ce',
								height: 30,
								padding: '0 10px',
							} : {
								fontSize: 12,
								border: '1px solid #d1d5db',
								background: '#f3f4f6',
								color: '#9ca3af',
								height: 30,
								padding: '0 10px',
								cursor: 'not-allowed',
								opacity: 1,
							}}
						>
							<span style={{
								display: 'flex',
								alignItems: 'center',
								gap: 5,
								fontWeight: 600,
								color: isButtonEnabled ? '#7e22ce' : '#9ca3af',
							}}>
								{isLoading
									? <Spin indicator={<LoadingOutlined spin style={{ fontSize: 14, color: '#7e22ce' }} />} />
									: <span
										className="material-symbols-outlined"
										style={{ fontSize: 15, color: isButtonEnabled ? '#7e22ce' : '#9ca3af' }}
									>
										auto_awesome
									</span>
								}
								Etiquetar con IA
							</span>
						</ButtonLZ>
					</div>

					{hasResult && (
						<div style={{
							display: 'flex',
							flexDirection: 'column',
							gap: 14,
							padding: '14px 16px 16px',
							borderRadius: 12,
							background: 'linear-gradient(180deg, #ede9fe 0%, #f5f3ff 38%, #ffffff 100%)',
							border: '1px solid #a78bfa',
							marginTop: 16,
						}}>
							<div style={{
								display: 'flex',
								alignItems: 'center',
								gap: 8,
								flexShrink: 0,
							}}>
								<span style={{
									display: 'inline-flex',
									alignItems: 'center',
									justifyContent: 'center',
									width: 28,
									height: 28,
									borderRadius: 8,
									background: '#7c3aed',
									color: '#ffffff',
									flexShrink: 0,
								}}>
									<span className="material-symbols-outlined" style={{ fontSize: 16, color: '#ffffff' }}>auto_awesome</span>
								</span>
								<div>
									<div style={{
										fontSize: 14,
										fontWeight: 700,
										color: '#0f172a',
										lineHeight: 1.3,
									}}>
										Etiquetas generadas con IA
									</div>
									<div style={{
										fontSize: 11,
										fontWeight: 500,
										color: '#94a3b8',
										lineHeight: 1.3,
										marginTop: 2,
									}}>
										Sugerencia a partir de la configuración de categorías
									</div>
								</div>
							</div>

							{labels?.length > 0 ? (
								<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
									{labels.map((label: any, index: number) => {
										const sentimentKey = label.sentiment;
										const sentimentText = SENTIMENT_LABEL[sentimentKey] || sentimentKey;
										const sentimentColor = LABEL_COLOR[sentimentKey] || '#64748b';

										return (
											<div
												key={`${label.category}-${label.label}-${index}`}
												style={{
													display: 'flex',
													alignItems: 'center',
													flexWrap: 'wrap',
													gap: 8,
													padding: '8px 10px',
													borderRadius: 8,
													background: '#ffffff',
													border: '1px solid rgba(148, 163, 184, 0.25)',
												}}
											>
												<LabelView label={label} />
												<span style={{ fontSize: 12, color: '#64748b' }}>
													Categoría: <b style={{ color: '#334155', fontWeight: 600 }}>{label.category}</b>
												</span>
												<span style={{ fontSize: 12, color: '#94a3b8' }}>·</span>
												<span style={{ fontSize: 12, color: '#64748b' }}>
													Sentimiento:{' '}
													<b style={{ color: sentimentColor, fontWeight: 600 }}>{sentimentText}</b>
												</span>
											</div>
										);
									})}
								</div>
							) : (
								<div style={{
									fontSize: 13,
									fontWeight: 500,
									color: '#64748b',
									lineHeight: 1.45,
									padding: '4px 2px',
								}}>
									No hay etiquetas para este texto
								</div>
							)}
						</div>
					)}
				</section>
			</div>
		</Drawer>
	)
}
